// Shared behaviour for LTL forms marked with [data-ltl-form].
// Progressive enhancement: without this script the markup still validates natively
// (required / type=email) and Shopify validates on the server. With it, errors appear inline
// under each field and in an announced summary, and the submit button shows a sending state.
//
// Forms with [data-ltl-klaviyo] submit asynchronously to Klaviyo's documented client-side
// endpoints (https://a.klaviyo.com/client/...), which take the public Site ID only. No private
// key is ever used here. Their submit button stays disabled until this script enables it, so
// nothing is sent anywhere without it.
(() => {
  const KLAVIYO_REVISION = '2026-07-15';
  const MIN_FILL_MS = 2000;
  const EVENT_PREFIX = { join: 'join_society', os: 'os_waitlist' };

  const fieldsOf = (form) =>
    [...form.querySelectorAll('input, textarea, select')].filter((el) => el.willValidate && el.type !== 'hidden');

  const messageFor = (el) => {
    if (el.validity.valueMissing) return el.dataset.msgRequired || el.validationMessage;
    if (el.validity.typeMismatch || el.validity.patternMismatch) return el.dataset.msgInvalid || el.validationMessage;
    return el.validationMessage;
  };

  const showError = (el, message) => {
    const box = el.id ? document.getElementById(`${el.id}-error`) : null;
    if (message) {
      el.setAttribute('aria-invalid', 'true');
      if (box) {
        box.textContent = message;
        box.hidden = false;
      }
    } else {
      el.removeAttribute('aria-invalid');
      if (box) {
        box.textContent = '';
        box.hidden = true;
      }
    }
  };

  const setBusy = (form, busy) => {
    const submit = form.querySelector('[type="submit"]');
    const label = submit && submit.querySelector('span');
    if (busy) {
      form.setAttribute('aria-busy', 'true');
      form.dataset.busy = 'true';
      if (label && submit.dataset.submittingLabel) {
        submit.dataset.idleLabel = label.textContent;
        label.textContent = submit.dataset.submittingLabel;
      }
    } else {
      form.removeAttribute('aria-busy');
      delete form.dataset.busy;
      if (label && submit.dataset.idleLabel) label.textContent = submit.dataset.idleLabel;
      if (submit) submit.disabled = false;
    }
  };

  const publish = (name, payload) => {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, payload || {});
      }
    } catch (error) {
      // Analytics never blocks a signup.
    }
  };

  const klaviyoPost = (path, companyId, body) =>
    fetch(`https://a.klaviyo.com/client/${path}?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body),
    });

  // Network errors, 429 and 5xx are retried after each delay; any other response is final.
  const RETRY_DELAYS_MS = [800, 2000];
  const klaviyoPostWithRetry = async (path, companyId, body) => {
    for (let attempt = 0; ; attempt += 1) {
      let response = null;
      let failure = null;
      try {
        response = await klaviyoPost(path, companyId, body);
      } catch (error) {
        failure = error;
      }
      const retryable = !response || response.status === 429 || response.status >= 500;
      if (!retryable || attempt >= RETRY_DELAYS_MS.length) {
        if (response) return response;
        throw failure;
      }
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
    }
  };

  // Production automation only ever acts on events with test_signup = false, which only the published
  // (live) theme produces. Any copy of a form served by an unpublished or development theme, such as a
  // preview used for QA, marks its events true so flows ignore them. If Shopify ever stops reporting
  // the theme role, every signup is marked as a test: automation stops rather than mis-enrolling
  // anyone (docs/KLAVIYO-TESTING.md).
  const themeRole = () => (window.Shopify && Shopify.theme && Shopify.theme.role) || 'unknown';
  const isTestSignup = () => themeRole() !== 'main';

  const newEventId = () =>
    window.crypto && typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const submitters = {
    // Join the Society: explicit email marketing consent, list membership, optional first name and
    // only the interests the visitor ticked (never written as false, so earlier choices survive),
    // then an "LTL Join Society Signup" event: the authoritative Join attribution and the trigger of
    // the welcome flow. The signup only counts as successful once BOTH are recorded, so a subscriber
    // can never be left without the event that enrols them:
    // - each call is retried on network errors, 429 and 5xx;
    // - if the event still fails, the visitor sees the error and can submit again; a step that
    //   already succeeded is remembered for the same details and never repeated;
    // - the event carries a unique_id fixed per signup, so Klaviyo keeps one event even when a
    //   request reached Klaviyo but its response was lost and the event is sent again.
    // test_signup marks signups made on any theme other than the published one (see themeRole).
    join: async (form) => {
      const data = new FormData(form);
      const email = String(data.get('email') || '').trim();
      const firstName = String(data.get('first_name') || '').trim();
      const interests = data.getAll('interests').map(String);
      const key = [email.toLowerCase(), firstName, interests.join(',')].join('|');
      if (!form.ltlSteps || form.ltlSteps.key !== key) {
        form.ltlSteps = { key, done: new Set(), eventId: newEventId(), joinedAt: new Date().toISOString() };
      }
      const { done, eventId, joinedAt } = form.ltlSteps;

      if (!done.has('subscribe')) {
        const properties = { ltl_joined_society_at: joinedAt };
        interests.forEach((interest) => {
          properties[`ltl_interest_${interest}`] = true;
        });
        const profile = { email, properties, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } };
        if (firstName) profile.first_name = firstName;

        const subscription = await klaviyoPostWithRetry('subscriptions', form.dataset.companyId, {
          data: {
            type: 'subscription',
            // No custom_source: Klaviyo copies it into the profile's $source, which overwrites the
            // existing value on profiles that already exist. Attribution is the event below.
            attributes: { profile: { data: { type: 'profile', attributes: profile } } },
            relationships: { list: { data: { type: 'list', id: form.dataset.listId } } },
          },
        });
        if (!subscription.ok) throw new Error(`Klaviyo subscription failed (${subscription.status})`);
        done.add('subscribe');
      }

      if (!done.has('event')) {
        const event = await klaviyoPostWithRetry('events', form.dataset.companyId, {
          data: {
            type: 'event',
            attributes: {
              properties: {
                form: 'join_band',
                page: window.location.pathname,
                interests,
                consent_given: true,
                test_signup: isTestSignup(),
                theme_role: themeRole(),
              },
              time: joinedAt,
              unique_id: eventId,
              metric: { data: { type: 'metric', attributes: { name: 'LTL Join Society Signup' } } },
              profile: { data: { type: 'profile', attributes: { email } } },
            },
          },
        });
        if (!event.ok) throw new Error(`Klaviyo event failed (${event.status})`);
        done.add('event');
      }
    },
  };

  // Lift To Live OS waitlist. Joining the waitlist is NOT marketing consent:
  // 1. /client/subscriptions only when the optional box is ticked (consent + list membership);
  // 2. /client/profiles always: ltl_interest_os and ltl_os_waitlist_joined_at (+ first name);
  //    this endpoint has no subscriptions field and never changes consent;
  // 3. /client/events always, last: "LTL OS Waitlist Signup", so the event only exists once
  //    everything before it succeeded and marketing_consent_given matches what was recorded.
  // Steps that already succeeded are remembered for the same email and consent choice, so a
  // retry after a partial failure never repeats them (no duplicate event, no timestamp reset).
  // The event carries the same test_signup marker as Join; no list ID is ever sent without consent.
  submitters.os = async (form) => {
    const data = new FormData(form);
    const email = String(data.get('email') || '').trim();
    const firstName = String(data.get('first_name') || '').trim();
    const consent = data.get('email_consent') === 'yes';
    const key = `${email.toLowerCase()}|${consent}`;
    if (!form.ltlSteps || form.ltlSteps.key !== key) form.ltlSteps = { key, done: new Set() };
    const { done } = form.ltlSteps;

    if (consent && !done.has('subscribe')) {
      const subscription = await klaviyoPost('subscriptions', form.dataset.companyId, {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: { type: 'profile', attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } },
            },
          },
          relationships: { list: { data: { type: 'list', id: form.dataset.listId } } },
        },
      });
      if (!subscription.ok) throw new Error(`Klaviyo subscription failed (${subscription.status})`);
      done.add('subscribe');
    }

    if (!done.has('profile')) {
      const attributes = {
        email,
        properties: { ltl_interest_os: true, ltl_os_waitlist_joined_at: new Date().toISOString() },
      };
      if (firstName) attributes.first_name = firstName;
      const profile = await klaviyoPost('profiles', form.dataset.companyId, { data: { type: 'profile', attributes } });
      if (!profile.ok) throw new Error(`Klaviyo profile failed (${profile.status})`);
      done.add('profile');
    }

    if (!done.has('event')) {
      const event = await klaviyoPost('events', form.dataset.companyId, {
        data: {
          type: 'event',
          attributes: {
            properties: {
              form: 'os_waitlist',
              page: window.location.pathname,
              marketing_consent_given: consent,
              test_signup: isTestSignup(),
              theme_role: themeRole(),
            },
            metric: { data: { type: 'metric', attributes: { name: 'LTL OS Waitlist Signup' } } },
            profile: { data: { type: 'profile', attributes: { email } } },
          },
        },
      });
      if (!event.ok) throw new Error(`Klaviyo event failed (${event.status})`);
      done.add('event');
    }
  };

  const showSuccess = (form) => {
    const fields = form.querySelector('[data-form-fields]');
    const summary = form.querySelector('[data-form-summary]');
    const success = form.querySelector('[data-form-success]');
    if (fields) fields.hidden = true;
    if (summary) summary.hidden = true;
    if (success) {
      success.hidden = false;
      success.focus();
    }
  };

  const showFailure = (form) => {
    const banner = form.querySelector('[data-form-error]');
    const more = form.querySelector('[data-form-error-more]');
    form.dataset.failures = String(Number(form.dataset.failures || 0) + 1);
    if (banner) {
      banner.hidden = false;
      if (more && Number(form.dataset.failures) >= 2) more.hidden = false;
      banner.setAttribute('tabindex', '-1');
      banner.focus();
    }
  };

  const submitAsync = async (form, kind) => {
    if (form.dataset.busy) return;
    const banner = form.querySelector('[data-form-error]');
    if (banner) banner.hidden = true;
    const trap = form.querySelector('input[name="company"]');
    const tooFast = Date.now() - Number(form.dataset.renderedAt || 0) < MIN_FILL_MS;

    setBusy(form, true);
    form.querySelector('[type="submit"]').disabled = true;
    publish(`${EVENT_PREFIX[kind]}_submitted`);

    // Likely bots: show the normal confirmation without sending anything.
    if ((trap && trap.value) || tooFast) {
      showSuccess(form);
      return;
    }

    try {
      await submitters[kind](form);
      showSuccess(form);
      publish(`${EVENT_PREFIX[kind]}_success`);
    } catch (error) {
      setBusy(form, false);
      showFailure(form);
    }
  };

  const enhance = (form) => {
    if (form.dataset.ltlFormReady) return;
    form.dataset.ltlFormReady = 'true';
    form.dataset.renderedAt = String(Date.now());
    form.noValidate = true;

    const kind = form.dataset.ltlKlaviyo;
    const asyncKind = kind && submitters[kind] ? kind : null;
    if (asyncKind) form.querySelectorAll('[type="submit"]').forEach((button) => (button.disabled = false));

    const summary = form.querySelector('[data-form-summary]');
    const submit = form.querySelector('[type="submit"]');

    const clearIfValid = (event) => {
      const el = event.target;
      if (el.getAttribute && el.getAttribute('aria-invalid') === 'true' && el.checkValidity()) showError(el, '');
    };
    form.addEventListener('input', clearIfValid);
    form.addEventListener('change', clearIfValid);

    form.addEventListener('submit', (event) => {
      const invalid = [];
      fieldsOf(form).forEach((el) => {
        const valid = el.checkValidity();
        showError(el, valid ? '' : messageFor(el));
        if (!valid) invalid.push(el);
      });

      if (invalid.length) {
        event.preventDefault();
        if (summary) {
          summary.textContent = summary.dataset.message;
          summary.hidden = false;
        }
        invalid[0].focus();
        return;
      }

      if (summary) summary.hidden = true;

      if (asyncKind) {
        event.preventDefault();
        submitAsync(form, asyncKind);
        return;
      }

      // Native submission (e.g. Shopify contact form).
      form.setAttribute('aria-busy', 'true');
      if (submit) {
        const label = submit.querySelector('span');
        if (label && submit.dataset.submittingLabel) label.textContent = submit.dataset.submittingLabel;
        // Defer so the browser submits the form before the button is disabled.
        setTimeout(() => {
          submit.disabled = true;
        }, 0);
      }
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('form[data-ltl-form]').forEach(enhance);
    // After a server round trip (success or Shopify errors), move focus to the result.
    const status = root.querySelector('[data-form-status-focus]');
    if (status) status.focus();
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
